import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import BASE_URL from "../BaseUrl/apiConfig";
import { useHistory } from "react-router-dom";

const AddNewMessageLogModal = ({
  showModal,
  setShowModal,
  type: initialType,
  recipientName: initialRecipientName,
  ccEmail: initialCcEmail,
  recipientEmailOrLink: initialRecipientEmailOrLink,
  senderEmail: initialSenderEmail,
  startDate: initialStartDate,
  messageStatus: initialMessageStatus,
  message: initialMessage,
  description: initialDescription,
  selectedMessageLog,
  updateData,
  resetForm,
}) => {
  const history = useHistory();
  const getCurrentISTDate = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is +5:30 hours from UTC
    const istDate = new Date(now.getTime() + istOffset);
    return istDate;
  };
  const [type, setType] = useState("Email");
  const [recipientName, setRecipientName] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [recipientEmailOrLink, setRecipientEmailOrLink] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [messageStatus, setMessageStatus] = useState("Primary Message");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState(initialDescription || "");
  const [errors, setErrors] = useState({});
  const [emailOptions, setEmailOptions] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    if (showModal) {
      setErrors({});
      if (selectedMessageLog) {
        setType(selectedMessageLog.type || "Email");
        setRecipientName(selectedMessageLog.recipientName || "");
        setRecipientEmailOrLink(selectedMessageLog.recipientEmailOrLink || "");
        setCcEmail(selectedMessageLog.ccEmail || "");
        setSenderEmail(selectedMessageLog.senderEmail || "");
        setStartDate(
          new Date(selectedMessageLog.messageDate) || getCurrentISTDate()
        );
        setMessageStatus(selectedMessageLog.messageStatus || "Primary Message");
        setMessage(selectedMessageLog.message || "");
        setDescription(selectedMessageLog.description || "");
      } else {
        setType(initialType || "Email");
        setRecipientName(initialRecipientName || "");
        setCcEmail(initialCcEmail || "");
        setRecipientEmailOrLink(initialRecipientEmailOrLink || "");
        setSenderEmail(initialSenderEmail || "");
        setStartDate(initialStartDate || getCurrentISTDate());
        setMessageStatus(initialMessageStatus || "Primary Message");
        setMessage(initialMessage || "");
        setDescription(initialDescription || "");
      }
    }
  }, [
    showModal,
    selectedMessageLog,
    initialType,
    initialRecipientName,
    initialRecipientEmailOrLink,
    initialCcEmail,
    initialSenderEmail,
    initialStartDate,
    initialMessageStatus,
    initialMessage,
    initialDescription,
  ]);

  useEffect(() => {
    const fetchEmailOptions = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const groupId = userData ? userData.groupId : null;

        if (groupId === null) {
          throw new Error("Group ID is not available in local storage");
        }

        const response = await fetch(
          `${BASE_URL}/api/Expense/GetAllEmailSMTPForDD?GroupID=${groupId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        setEmailOptions(data);
      } catch (error) {
        console.error("Error fetching email options:", error);
      }
    };

    fetchEmailOptions();
  }, []);

  const fetchMessageLogs = () => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const groupId = userData ? userData.groupId : null;

    fetch(`${BASE_URL}/api/Expense/GetAllMesaageLog?UserID=${groupId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // setMessageLogs(data);
        updateData();
        setErrors("");
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    if (name === "type") setType(value);
    if (name === "recipientName") setRecipientName(value);
    if (name === "recipientEmailOrLink") setRecipientEmailOrLink(value);
    if (name === "ccEmail") setCcEmail(value);
    if (name === "senderEmail") {
      setSenderEmail(value);
      // No confirmation dialog here
    }
    if (name === "description") setDescription(value);
    if (name === "messageStatus") setMessageStatus(value);
  };

  const handleDateChange = (date) => {
    setStartDate(date);
  };

  const handleMessageChange = (value) => {
    setMessage(value);
  };

  const validateFields = () => {
    const newErrors = {};

    if (!type) {
      newErrors.type = "Type is required";
      resetSenderEmailAndConfirmation();
    }

    if (!recipientName) {
      newErrors.recipientName = "Recipient Name is required";
      resetSenderEmailAndConfirmation();
    }

    if (!recipientEmailOrLink) {
      newErrors.recipientEmailOrLink =
        "Recipient Email 'or' Profile Links are required";
      resetSenderEmailAndConfirmation();
    }

    if (!startDate) {
      newErrors.messageDate = "Message Date is required";
      resetSenderEmailAndConfirmation();
    }

    const messageString = typeof message === "string" ? message : "";
    if (messageString.trim() === "<p><br></p>" || messageString.trim() === "") {
      newErrors.message = "Message content is required";
      resetSenderEmailAndConfirmation();
    }

    if (!messageStatus) {
      newErrors.messageStatus = "Message Status is required";
      resetSenderEmailAndConfirmation();
    }

    if (type === "Email") {
      if (isConfirmed && !senderEmail) {
        newErrors.senderEmail = "Sender Email is required if confirmed";
      }
      if (!description) {
        newErrors.description = "Subject is required for Email type";
      }
    }

    return newErrors;
  };

  // Helper function to reset senderEmail and isConfirmed
  const resetSenderEmailAndConfirmation = () => {
    setSenderEmail(""); // Assuming setSenderEmail is a state setter function
    setIsConfirmed(false); // Assuming setIsConfirmed is a state setter function
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
  
    // Validate fields
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    const userData = JSON.parse(localStorage.getItem("userData"));
    const payload = {
      recipientEmailOrLink,
      ccEmail, // Adding CcEmail to the payload
      subject: description,
      body: message,
      senderEmail,
      type,
      recipientName,
      messageDate: startDate.toISOString(),
      messageStatus,
      message,
      description,
      userId: userData.userId,
      groupId: userData.groupId,
    };
  
    try {
      if (type === "Email" && !isConfirmed && senderEmail) {
        const { isConfirmed } = await Swal.fire({
          title: "Confirm Email Sending",
          text: `Are you sure you want to send this email using ${senderEmail}?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Send it!",
          cancelButtonText: "Only save",
        });
  
        // Proceed only if confirmed
        if (isConfirmed) {
          // Split the recipient emails by comma
          const emailAddresses = recipientEmailOrLink.split(",");
  
          for (const email of emailAddresses) {
            // Trim whitespace and ensure non-empty email
            const trimmedEmail = email.trim();
            if (trimmedEmail) {
              // Update payload with the current email
              const emailPayload = {
                ...payload,
                recipientEmailOrLink: trimmedEmail, // Update to single email for each send
              };
  
              const emailResponse = await fetch(
                `${BASE_URL}/api/Expense/SendEmail`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(emailPayload),
                }
              );
  
              if (!emailResponse.ok) {
                throw new Error("Failed to send email");
              }
            }
          }
  
          Swal.fire(
            "Success",
            "Emails have been sent successfully.",
            "success"
          );
        } else {
          // If not confirmed, skip email sending but proceed to save the log
          // Save the message log data
          const logResponse = await fetch(
            `${BASE_URL}/api/Expense/AddUpdateMessageLog`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ...payload,
                messageID: selectedMessageLog
                  ? selectedMessageLog.messageID
                  : undefined,
              }),
            }
          );
  
          if (!logResponse.ok) {
            throw new Error("Failed to save message log");
          }
  
          // Show a unified success message
          Swal.fire(
            "Success",
            "Message log has been added successfully.",
            "success"
          );
  
          fetchMessageLogs();
          setShowModal(false);
          resetForm();
          return;
        }
      }
  
      // Save the message log data (for cases other than email sending or after emails sent)
      const logResponse = await fetch(
        `${BASE_URL}/api/Expense/AddUpdateMessageLog`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...payload,
            messageID: selectedMessageLog
              ? selectedMessageLog.messageID
              : undefined,
          }),
        }
      );
  
      if (!logResponse.ok) {
        throw new Error("Failed to save message log");
      }
  
      // Show a unified success message
      Swal.fire(
        "Success",
        "Message log has been added successfully.",
        "success"
      );
  
      fetchMessageLogs();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error:", error);
      Swal.fire(
        "Error",
        "An error occurred while processing your request.",
        "error"
      );
    }
  };
  

  const fetchSMTPSettings = async (senderEmail) => {
    try {
      console.log("Fetching SMTP settings for:", senderEmail); // Debug log
      const response = await fetch(
        `${BASE_URL}/api/Expense/GetSMTPSettingsBySenderEmail?senderEmail=${encodeURIComponent(
          senderEmail
        )}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch SMTP settings");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching SMTP settings:", error);
      return null;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    fetchMessageLogs();
  };

  return (
    <>
      {showModal && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-gray-900 opacity-50 z-50"></div>

          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg rounded-lg p-8 z-50">
            <h2 className="text-xl font-bold mb-4">Add New Message Log</h2>
            <form onSubmit={handleSubmit} className="row">
              <div className="form-group col-6 col-lg-2 mb-4">
                <select
                  className={`form-control custom-height ${
                    errors.type ? "is-invalid" : ""
                  }`}
                  name="type"
                  value={type}
                  onChange={handleInputChange}
                  title="Select the message type"
                >
                  <option value="">Select Type</option>
                  <option value="Email">Email</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Others">Others</option>
                </select>
                {errors.type && (
                  <div className="text-red-500">{errors.type}</div>
                )}
              </div>

              <div className="form-group col-6 col-lg-2 mb-4">
                <input
                  type="text"
                  name="recipientName"
                  placeholder="Recipient Name"
                  value={recipientName}
                  onChange={handleInputChange}
                  className={`form-control custom-height ${
                    errors.recipientName ? "is-invalid" : ""
                  }`}
                  title="Enter the recipient's name"
                />
                {errors.recipientName && (
                  <div className="text-red-500">{errors.recipientName}</div>
                )}
              </div>

              <div className="form-group col-6 col-lg-3 mb-4">
                <input
                  type="text"
                  name="recipientEmailOrLink"
                  placeholder="Recipient Email or Profile Links"
                  value={recipientEmailOrLink}
                  onChange={handleInputChange}
                  className={`form-control custom-height ${
                    errors.recipientEmailOrLink ? "is-invalid" : ""
                  }`}
                  title="Enter the recipient's email or profile link"
                />
                {errors.recipientEmailOrLink && (
                  <div className="text-red-500">
                    {errors.recipientEmailOrLink}
                  </div>
                )}
              </div>
              <div className="form-group col-6 col-lg-3 mb-4">
                <input
                  type="text"
                  name="ccEmail"
                  placeholder="CC Email"
                  value={ccEmail}
                  onChange={handleInputChange}
                  className={`form-control custom-height ${
                    errors.ccEmail ? "is-invalid" : ""
                  }`}
                  title="Enter comma-separated CC email addresses"
                  disabled={type !== "Email"}
                />
                {errors.ccEmail && (
                  <div className="text-red-500">{errors.ccEmail}</div>
                )}
              </div>
              <div className="form-group col-6 col-lg-2 mb-4">
                <DatePicker
                  selected={startDate}
                  onChange={handleDateChange}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  className={`form-control custom-height ${
                    errors.messageDate ? "is-invalid" : ""
                  }`}
                  popperPlacement="bottom"
                  popperModifiers={{
                    preventOverflow: {
                      enabled: false,
                    },
                    hide: {
                      enabled: false, // Ensures visibility of the popper
                    },
                  }}
                  title="Select date and time"
                />

                {errors.messageDate && (
                  <div className="text-red-500">{errors.messageDate}</div>
                )}
              </div>

              {type === "Email" && (
                <>
                  <div className="form-group col-12 col-lg-12 mb-4">
                    <textarea
                      name="description"
                      placeholder="Subject"
                      value={description}
                      onChange={handleInputChange}
                      className={`form-control custom-height ${
                        errors.description ? "is-invalid" : ""
                      }`}
                      title="Enter a Subject of Mail"
                    />
                    {errors.description && (
                      <div className="text-red-500">{errors.description}</div>
                    )}
                  </div>
                </>
              )}

              <div className="form-group col-12 col-lg-12 mb-4">
                <ReactQuill
                  value={message}
                  onChange={handleMessageChange}
                  placeholder="Message"
                  className={`custom-height ${
                    errors.message ? "is-invalid" : ""
                  }`}
                  title="Enter the message content"
                  style={{
                    border: "1px solid #ddd",
                    minHeight: "200px",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                />
                {errors.message && (
                  <div className="text-red-500">{errors.message}</div>
                )}
              </div>
              {type === "Email" && (
                <>
                  <div className="form-group col-6 col-lg-3 mb-4">
                    <select
                      className={`form-control custom-height ${
                        errors.senderEmail ? "is-invalid" : ""
                      }`}
                      name="senderEmail"
                      value={senderEmail}
                      onChange={handleInputChange}
                      title="Select Sender Email"
                    >
                      <option value="">Select Sender Email</option>
                      {emailOptions.map((option) => (
                        <option
                          key={option.settingID}
                          value={option.senderEmail}
                        >
                          {option.senderEmail}
                        </option>
                      ))}
                    </select>
                    {errors.senderEmail && (
                      <div className="text-red-500">{errors.senderEmail}</div>
                    )}
                  </div>
                </>
              )}
              <div className="col-12 text-right">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={handleCloseModal}
                  title="Close the modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  title="Save the message log"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default AddNewMessageLogModal;
